import { CheckIcon, DownloadIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { oneOfValidator, type ImportSchema } from '@/components/workbook/import-schema';
import {
  WorkbookImporter,
  type ImportPayload,
  type ServerValidation,
} from '@/components/workbook/WorkbookImporter';
import { api, errorMessage } from '@/lib/api';
import type { OrgImportResult } from '@/lib/types';
import { useImportJobRows } from './queries';

const STATUS_OPTIONS = ['active', 'archived', 'open', 'closed'];
const ACTION_OPTIONS = ['upsert', 'create', 'update', 'archive', 'restore'];

/**
 * Jobs import = the generic WorkbookImporter with the job schema and a debounced server dry run
 * (existing jobs, unknown team members, bad timezones). Jobs are matched by name; blank cells never
 * overwrite existing values, so an exported workbook can be edited and re-imported.
 */
export function JobsImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const importRows = useImportJobRows();
  const [updateExisting, setUpdateExisting] = useState(true);
  const [replaceTeams, setReplaceTeams] = useState(false);
  const [result, setResult] = useState<OrgImportResult | null>(null);

  useEffect(() => {
    if (!open) {
      setUpdateExisting(true);
      setReplaceTeams(false);
      setResult(null);
    }
  }, [open]);

  const schema = useMemo<ImportSchema>(
    () => ({
      noun: 'job',
      fields: [
        {
          key: 'name',
          label: 'Job name',
          required: true,
          unique: true,
          aliases: ['job', 'job name', 'project', 'project name', 'site', 'site name', 'title'],
          normalize: (v) => v.trim().toLowerCase() || null,
        },
        { key: 'description', label: 'Description', aliases: ['desc', 'notes'] },
        {
          key: 'senderLabel',
          label: 'Sender label',
          hint: 'Prefix shown to recipients, e.g. "Orlando Tower:"',
          aliases: ['sender', 'label', 'prefix'],
        },
        { key: 'address', label: 'Site address', aliases: ['site address', 'location'] },
        {
          key: 'timezone',
          label: 'Timezone',
          hint: 'IANA name, e.g. America/New_York',
          aliases: ['tz', 'time zone'],
        },
        {
          key: 'status',
          label: 'Status',
          hint: 'active or archived',
          aliases: ['state'],
          validate: oneOfValidator(STATUS_OPTIONS, 'status'),
        },
        {
          key: 'admins',
          label: 'Job admins',
          hint: 'Existing user emails, separated by ;',
          aliases: ['admin', 'job admins', 'admin emails'],
        },
        {
          key: 'members',
          label: 'Team members',
          hint: 'Existing user emails, separated by ;',
          aliases: ['member', 'users', 'team', 'member emails'],
        },
        {
          key: 'action',
          label: 'Action',
          hint: 'upsert (default), archive or restore',
          aliases: ['operation', 'op'],
          validate: oneOfValidator(ACTION_OPTIONS, 'action'),
        },
      ],
    }),
    [],
  );

  const toInput = useCallback(
    (p: ImportPayload, dryRun: boolean) => ({
      fileName: p.fileName,
      sheetName: p.sheetName,
      headers: p.headers,
      rows: p.rows,
      mapping: p.mapping,
      updateExisting,
      dryRun,
      membershipMode: replaceTeams ? ('replace' as const) : ('merge' as const),
    }),
    [updateExisting, replaceTeams],
  );

  const validateOnServer = useCallback(
    async (p: ImportPayload, signal: AbortSignal): Promise<ServerValidation> => {
      const r = await api.jobs.importRows(toInput(p, true), signal);
      return {
        rows: r.rowResults.map((x) => ({
          index: x.index,
          status: x.status,
          reason: x.reason,
          field: x.field,
        })),
        summary: [
          { label: 'new', value: r.created, tone: 'success' },
          { label: 'updated', value: r.updated, tone: 'secondary' },
          ...(r.deactivated > 0
            ? [{ label: 'archived', value: r.deactivated, tone: 'warning' as const }]
            : []),
          ...(r.reactivated > 0
            ? [{ label: 'restored', value: r.reactivated, tone: 'secondary' as const }]
            : []),
          { label: 'skipped', value: r.skipped, tone: 'muted' },
          ...(r.errorCount > 0
            ? [{ label: 'errors', value: r.errorCount, tone: 'danger' as const }]
            : []),
        ],
      };
    },
    [toInput],
  );

  const onImport = useCallback(
    async (p: ImportPayload) => {
      try {
        const r = await importRows.mutateAsync(toInput(p, false));
        setResult(r);
        toast.success(`Imported ${r.created} new and updated ${r.updated} jobs`);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    },
    [importRows, toInput],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[min(96vw,1240px)]">
        <DialogHeader>
          <DialogTitle>Import jobs</DialogTitle>
          <DialogDescription>
            Upload an Excel workbook or CSV, check the preview, fix anything highlighted, then
            import. Jobs are matched by name; blank cells leave existing values alone. Use the
            Status or Action column to archive or restore jobs, and Admins / Members to set each
            job's team.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="grid gap-5">
            <div className="flex items-center gap-3 border p-4">
              <CheckIcon className="text-success size-5" />
              <div className="grid gap-0.5 text-sm">
                <span className="font-medium">Import complete</span>
                <span className="text-muted-foreground text-xs">
                  {result.created} new · {result.updated} updated · {result.skipped} skipped ·{' '}
                  {result.errorCount} errors
                  {result.deactivated > 0 && ` · ${result.deactivated} archived`}
                  {result.reactivated > 0 && ` · ${result.reactivated} restored`}
                </span>
              </div>
            </div>
            {result.errors.length > 0 && (
              <ul className="text-muted-foreground max-h-48 overflow-y-auto border p-3 text-xs">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.reason}`}>
                    Row {e.row}: {e.reason}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <WorkbookImporter
            schema={schema}
            validateOnServer={validateOnServer}
            onImport={onImport}
            validationKey={`${updateExisting}|${replaceTeams}`}
            importLabel={({ rows, errors }) =>
              `Import ${Math.max(0, rows - errors).toLocaleString()} jobs`
            }
            pickerExtras={
              <>
                <span className="text-muted-foreground text-xs">Need a starting point?</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.jobs
                        .templateXlsx()
                        .catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> Excel template
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.jobs.template().catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> CSV template
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void api.jobs.exportXlsx().catch((e: unknown) => toast.error(errorMessage(e)))
                    }
                  >
                    <DownloadIcon /> Export current jobs
                  </Button>
                </div>
              </>
            }
            options={
              <div className="grid gap-4 border bg-card p-3">
                <h4 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Options
                </h4>
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={updateExisting}
                    onCheckedChange={(v) => setUpdateExisting(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Update existing jobs</span>
                    <span className="text-muted-foreground text-xs">
                      When a job with the same name exists, apply the filled-in cells instead of
                      skipping the row.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-3">
                  <Checkbox
                    checked={replaceTeams}
                    onCheckedChange={(v) => setReplaceTeams(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="grid gap-0.5">
                    <span className="text-sm font-medium">Replace teams</span>
                    <span className="text-muted-foreground text-xs">
                      Remove people who are not listed in Admins / Members. Off = only add or change
                      the listed people.
                    </span>
                  </span>
                </label>
              </div>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
